"use client";
import ScreenLayout from "@/components/layout/screen-layout";
import ServicePageMeta from "@/components/common/seo/service-page-meta";

export default function FAQsPage() {
    const faqs = [
        {
            question: "What are the most popular types of software development services offered by Devkrest?",
            answer: "Devkrest specializes in comprehensive software development services including mobile app development (iOS/Android), web application development, custom software solutions, Gen-AI integration services, product engineering, and enterprise software development. We also offer UI/UX design, cloud infrastructure, and digital transformation services."
        },
        {
            question: "Do you offer a risk-free trial period for new clients?",
            answer: "Yes, we offer a 15-day risk-free trial period that is completely free. This allows you to evaluate our development quality, communication style, and team collaboration before making a long-term commitment. During this period, you can assess our work and decide if we're the right fit for your project."
        },
        {
            question: "Can I be actively involved in the development process?",
            answer: "Absolutely! We encourage client involvement throughout the development process. You can participate in sprint planning, review milestones, provide feedback, and make decisions at every stage. We use collaborative tools and regular meetings to keep you informed and involved in all aspects of your project."
        },
        {
            question: "What hiring engagement options are available at Devkrest?",
            answer: "We offer flexible engagement models including dedicated team hiring, project-based contracts, hourly consulting, and full-time resource allocation. You can choose from individual developers, complete development teams, or hybrid models based on your specific requirements and budget."
        },
        {
            question: "What project management tools and methodologies do you use?",
            answer: "We use industry-standard tools including Jira for project tracking, Confluence for documentation, Slack for communication, and GitHub/GitLab for version control. Our methodology is primarily Agile/Scrum with regular sprint planning, daily standups, and sprint retrospectives to ensure efficient project delivery."
        },
        {
            question: "How do you ensure the security and confidentiality of our intellectual property?",
            answer: "We implement strict security measures including NDAs, secure development environments, encrypted communications, and access controls. All team members sign confidentiality agreements, and we follow industry best practices for data protection and IP security throughout the development process."
        },
        {
            question: "What if I'm not satisfied with the hired developer's work quality?",
            answer: "If you're not satisfied with a developer's work, we offer immediate replacement with a more suitable resource at no additional cost. We also provide a satisfaction guarantee and will work to resolve any concerns through our dedicated support team to ensure your project meets your expectations."
        },

        {
            question: "Do you provide ongoing support and maintenance services after project deployment?",
            answer: "Yes, we offer comprehensive post-deployment support including bug fixes, feature updates, performance optimization, security patches, and 24/7 monitoring. Our maintenance packages include regular updates, technical support, and ongoing improvements to keep your software running smoothly."
        },
        {
            question: "What services does Devkrest Technologies offer?",
            answer: "Devkrest Technologies offers comprehensive software development services including mobile app development, web application development, custom software development, custom CRMs, Gen-AI services, and product engineering solutions."
        },
        {
            question: "Do you provide ongoing support and maintenance?",
            answer: "Yes, we offer comprehensive post-launch support and maintenance services to ensure your software continues to perform optimally and stays updated with the latest technologies."
        },
        {
            question: "How do you ensure the quality of your deliverables?",
            answer: "We follow industry best practices including code reviews, automated testing, continuous integration, and thorough quality assurance processes to ensure high-quality deliverables."
        },
        {
            question: "Do you provide project updates and communication?",
            answer: "Yes, we maintain transparent communication throughout the project with regular updates, milestone reports, and dedicated project managers to keep you informed of progress."
        },
        {
            question: "What is your approach to project management?",
            answer: "We follow agile methodologies with regular sprints, client feedback integration, and iterative development to ensure your vision is accurately translated into the final product."
        },
        {
            question: "Do you offer consultation services?",
            answer: "Yes, we provide free initial consultations to understand your requirements, discuss potential solutions, and provide recommendations for the best approach to achieve your goals."
        }
    ];

    return (
        <>
            <ServicePageMeta
                title="Frequently Asked Questions | Devkrest Development Services FAQ"
                description="Find answers to common questions about Devkrest's software development services, project processes, team structure, and engagement models. Get clarity on development timelines, pricing, quality assurance, and post-deployment support."
                keywords={[
                    "frequently asked questions",
                    "FAQ",
                    "software development FAQ",
                    "development services questions",
                    "project management FAQ",
                    "development process questions",
                    "team hiring FAQ",
                    "quality assurance questions",
                    "support and maintenance FAQ",
                    "development consultation",
                    "project delivery FAQ",
                    "development methodology questions",
                    "Devkrest FAQ"
                ]}
                serviceType="FAQ"
                url="/faqs"
                image="/images/faqs/faqs-hero.webp"
            />
            <main className="min-h-screen bg-white">
                {/* Hero Section */}
                <div className="relative pt-40 md:pt-44 lg:pt-48 pb-20 overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-white">
                    {/* Background Elements */}
                    <div className="absolute inset-0 w-full h-full">
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(82,49,231,0.02)_1.6px,transparent_1.6px),linear-gradient(to_right,rgba(82,49,231,0.02)_1.6px,transparent_1.6px)] bg-[size:24px_24px]" />
                        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-purple-400/20 to-blue-400/20 rounded-full blur-xl opacity-50" />
                        <div className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl opacity-50" />
                    </div>

                    {/* Content Container */}
                    <div className="relative z-10">
                        <ScreenLayout>
                            <div className="max-w-4xl mx-auto">
                                <div className="space-y-6 text-center">
                                    {/* Main Heading */}
                                    <div className="space-y-4">
                                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.1]">
                                            <span className="text-gray-900">Frequently Asked</span>{" "}
                                            <span className="bg-gradient-to-r from-[#5231E7] to-[#764AF1] bg-clip-text text-transparent">
                                                Questions
                                            </span>
                                        </h1>
                                    </div>

                                    {/* Subtitle */}
                                    <div className="space-y-4">
                                        <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                                            Get answers to everything you need to know about our development services, processes, and how we can transform your ideas into powerful software solutions.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </ScreenLayout>
                    </div>
                </div>

                {/* Content Section */}
                <div className="py-12 md:py-16">
                    <ScreenLayout>
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
                                <div className="prose prose-lg max-w-none">
                                    {/* Introduction */}
                                    <p className="text-gray-700 leading-relaxed mb-8">
                                        We've compiled answers to the most common questions about our development services, team structure, and project delivery process. Whether you're looking to hire developers, start a new project, or understand our approach, you'll find everything you need to know below.
                                    </p>

                                    {/* FAQs */}
                                    <div className="space-y-12">
                                        {faqs.map((faq, index) => (
                                            <div key={index} className="mb-12">
                                                <div className="text-xl flex flex-row md:text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                                                    <div className="w-8 h-8 bg-gradient-to-r from-[#5231E7] to-[#764AF1] text-white rounded-[50%] flex items-center justify-center text-sm font-bold mr-4">
                                                        {index + 1}
                                                    </div>
                                                    <h2 className="flex-1">{faq.question}</h2>
                                                </div>
                                                <p className="text-gray-600 leading-relaxed">
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Contact Section */}
                                    <div className="mt-16 pt-8 border-t border-gray-200">
                                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center">
                                            <span className="w-8 h-8 bg-gradient-to-r from-[#5231E7] to-[#764AF1] text-white rounded-[50%] flex items-center justify-center text-sm font-bold mr-4">
                                                {faqs.length + 1}
                                            </span>
                                            Ready to start your project?
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed mb-6">
                                            Have more questions or ready to discuss your project requirements? Our team of experts is here to help you bring your vision to life with cutting-edge software solutions.
                                        </p>
                                        <p className="text-gray-700 leading-relaxed">
                                            Get in touch with us at:{" "}
                                            <a href="mailto:hello@devkrest.com" className="text-[#5231E7] hover:text-[#764AF1] font-medium transition-colors duration-200">
                                                hello@devkrest.com
                                            </a>
                                            {" "}or explore our{" "}
                                            <a href="/contact-us" className="text-[#5231E7] hover:text-[#764AF1] font-medium transition-colors duration-200">
                                                contact page
                                            </a>
                                            {" "}to schedule a consultation with our development experts.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ScreenLayout>
                </div>
            </main>
        </>
    );
} 